import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { cn } from '~/lib/utils';
import { CaretRightIcon } from '@radix-ui/react-icons';

const transition = {
  type: 'spring',
  mass: 0.5,
  damping: 11.5,
  stiffness: 100,
  restDelta: 0.001,
  restSpeed: 0.001,
};

export const MenuItem = ({
  setActive,
  active,
  item,
  children,
  href,
}: {
  setActive?: (item: string | null) => void;
  active?: string | null;
  item: string;
  children?: React.ReactNode;
  className?: string;
  href?: string;
}) => {
  const toggle = (item: string, on?: boolean) => {
    if (on && active != item) {
      return setActive?.(item);
    }

    if (!on && active === item) {
      return setActive?.(null);
    }

    if (on === undefined && active !== item) {
      return setActive?.(item);
    }
  };

  return (
    <div
      onMouseEnter={() => toggle(item, true)}
      onMouseLeave={() => toggle(item, false)}
      onClick={() => toggle(item)}
      className="relative"
    >
      <div>
        {href ? (
          <Link
            href={href}
            className="cursor-pointer text-zinc-700 hover:text-zinc-900 dark:text-zinc-200 dark:hover:text-zinc-50"
          >
            {item}{' '}
            <CaretRightIcon
              className={cn(`inline h-4 w-4 transition-transform`, {
                'rotate-90': active,
              })}
            />
          </Link>
        ) : (
          <span className="cursor-pointer text-zinc-700 hover:text-zinc-900 dark:text-zinc-200 dark:hover:text-zinc-50">
            {item}{' '}
            <CaretRightIcon
              className={cn(`inline h-4 w-4 transition-transform`, {
                'rotate-90': active,
              })}
            />
          </span>
        )}
      </div>
      {active === item && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={transition}
          style={{ zIndex: 90_000 }}
        >
          <div className="absolute left-1/2 top-[calc(100%_+_0rem)] z-50 -translate-x-1/2 transform pt-6">
            <motion.div
              transition={transition}
              layoutId="active" // layoutId ensures smooth animation
              className="overflow-hidden rounded-2xl border border-black/[0.2] bg-white shadow-xl backdrop-blur-sm dark:border-white/[0.2] dark:bg-black"
              style={{ zIndex: 90_000 }}
            >
              <motion.div
                layout // layout ensures smooth animation
                className="h-full w-max p-4"
              >
                {children}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export const Menu = ({
  setActive,
  children,
}: {
  setActive: (item: string | null) => void;
  children: React.ReactNode;
}) => {
  return (
    <nav
      onMouseLeave={() => setActive(null)} // resets the state
      className="relative hidden flex-1 flex-row items-center justify-center space-x-2 text-zinc-700 transition duration-200 hover:text-zinc-900 dark:text-zinc-200 dark:hover:text-zinc-50  md:flex md:space-x-6"
    >
      {children}
    </nav>
  );
};
